import { useState } from "react";

import {
  Close,
  CompareArrows,
  CheckCircle,
  Visibility,
  RemoveCircleOutline,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { postIntelImageMatch } from "@rubi/api-generated";
import { colors } from "@rubi/ui";
import { toast } from "@rubi/ui";

import MediaDisplay from "@/common/MediaDisplay";
import { useImageMatch } from "@/contexts/ImageMatchContext";

const ImageMatchBanner = () => {
  const {
    isMatchInProgress,
    sourceAttachment,
    targetAttachments,
    removeTarget,
    cancelMatch,
    completeMatch,
  } = useImageMatch();

  const [reviewOpen, setReviewOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isMatchInProgress || !sourceAttachment) {
    return null;
  }

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await Promise.all(
        targetAttachments.map((target) =>
          postIntelImageMatch({
            body: {
              submission_id_1: sourceAttachment.submissionId,
              attachment_id_1: sourceAttachment.attachmentId,
              submission_id_2: target.submissionId,
              attachment_id_2: target.attachmentId,
              notes: null,
            },
          }),
        ),
      );
      toast.success(
        `Match created with ${targetAttachments.length} attachment(s)`,
      );
      completeMatch();
      setCompleteOpen(false);
    } catch (error) {
      console.error("Failed to create image match:", error);
      toast.error("Failed to create image match. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 11,
          backgroundColor: colors.primary.dark,
          color: colors.common.white,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.25)",
          flexWrap: "wrap",
        }}
      >
        <CompareArrows sx={{ fontSize: 24 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
          Image Match In Progress
        </Typography>

        <Chip
          label={`Source: ${sourceAttachment.filename}`}
          size="small"
          onClick={() => setReviewOpen(true)}
          icon={<Visibility sx={{ color: "white !important", fontSize: 16 }} />}
          sx={{
            backgroundColor: alpha(colors.common.white, 0.2),
            color: colors.common.white,
            cursor: "pointer",
            "&:hover": { backgroundColor: alpha(colors.common.white, 0.3) },
            "& .MuiChip-icon": { color: colors.common.white },
          }}
        />

        {targetAttachments.length > 0 && (
          <Chip
            label={`${targetAttachments.length} target(s) selected`}
            size="small"
            sx={{
              backgroundColor: "rgba(76,175,80,0.3)",
              color: colors.common.white,
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        {targetAttachments.length > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircle />}
            onClick={() => setCompleteOpen(true)}
            sx={{
              backgroundColor: colors.success.light,
              "&:hover": { backgroundColor: colors.success.main },
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Complete Match ({targetAttachments.length})
          </Button>
        )}

        <Tooltip title="Cancel match">
          <IconButton
            onClick={cancelMatch}
            size="small"
            sx={{ color: colors.common.white }}
          >
            <Close />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Review Source Attachment Modal */}
      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CompareArrows />
          Source Attachment
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setReviewOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 2,
              maxHeight: 500,
              overflow: "hidden",
            }}
          >
            <MediaDisplay
              src={sourceAttachment.presignedUrl}
              mediaType={
                sourceAttachment.contentType.includes("image")
                  ? "image"
                  : "video"
              }
              onLoad={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.maxHeight = "500px";
                target.style.objectFit = "contain";
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {sourceAttachment.filename}
          </Typography>

          {targetAttachments.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Targets ({targetAttachments.length})
              </Typography>
              {targetAttachments.map((target) => (
                <Chip
                  key={target.attachmentId}
                  label={target.filename}
                  size="small"
                  onDelete={() => removeTarget(target.attachmentId)}
                  deleteIcon={<RemoveCircleOutline sx={{ fontSize: 16 }} />}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete Match Confirmation Modal */}
      <Dialog
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Image Match</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create a match between the source attachment and{" "}
            {targetAttachments.length} selected target(s)?
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Source
            </Typography>
            <Chip label={sourceAttachment.filename} size="small" />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Targets
            </Typography>
            {targetAttachments.map((target) => (
              <Chip
                key={target.attachmentId}
                label={target.filename}
                size="small"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            ))}
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
          >
            <Button
              onClick={() => setCompleteOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleComplete}
              disabled={isSubmitting}
              startIcon={<CheckCircle />}
              sx={{ textTransform: "none" }}
            >
              {isSubmitting ? "Creating..." : "Create Match"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageMatchBanner;
